/* eslint camelcase: ["error", {allow: ["^UNSAFE_"]}] */
/**
 * @flow
 */
import React from 'react';
import type { ViewportType } from '@ziynx/flow-types/viewports';
import type { InjectedProps } from '@ziynx/flow-types/appManager';
import ViewportManager from './ViewportManager';
import SideNavManager from './SideNavManager';
import _isStyledComponent from '../utils/isStyledComponent';

const CHARACTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

type InnerState = {
  viewport?: ViewportType,
  sideNavExpanded?: boolean,
}

type RefProps = $ReadOnly<{|
  innerRef?: React$Ref<any>,
  ref?: React$Ref<any>,
|}>

function wrapWithManager<Config: React$Config<any, any>>(
  Component: React$ComponentType<{| ...Config, ...RefProps, ...InjectedProps |}>,
): React$ComponentType<{| ...Config, ...InjectedProps|}> {
  const componentName: string = Component?.displayName || Component?.name || 'Component';
  const isStatelessFunctionalComponent = typeof Component === 'function'
  && !(Component.prototype && 'isReactComponent' in Component.prototype);

  // NOTE: We can't pass a ref to a stateless functional component
  const shouldSetInnerRef = _isStyledComponent(Component) || isStatelessFunctionalComponent;

  class WithManager extends React.Component<Config, InnerState> {
    viewportManager: typeof ViewportManager;

    sideNavManager: typeof SideNavManager;

    id: string;

    result: string;

    constructor(props: {| ...Config, ...InjectedProps |}) {
      super(props);
      this.viewportManager = ViewportManager;
      this.sideNavManager = SideNavManager;
      this.state = {};
      this.id = this.makeId(10);
    }

    componentDidMount() {
      this.viewportManager.subscribe((this.id), (vp: ViewportType) => {
        this.setViewport(vp);
      });
      this.sideNavManager.subscribe((this.id), (isExpanded: boolean) => {
        this.setSideNavState(isExpanded);
      });
      // for intial viewport & side nav state
      this.setState({
        viewport: this.viewportManager.viewport,
        sideNavExpanded: this.sideNavManager.sideNavExpanded,
      });
    }

    componentWillUnmount() {
      this.viewportManager.unsubscribe(this.id);
      this.sideNavManager.unsubscribe(this.id);
    }

    setViewport(vp: ViewportType): void {
      this.setState({ viewport: vp });
    }

    setSideNavState(isExpanded: boolean): void {
      this.setState({ sideNavExpanded: isExpanded });
    }

    sideNavAppHandler = (data: boolean) => {
      if (data !== this.sideNavManager.sideNavExpanded) {
        this.sideNavManager.sideNavToggled(data);
      }
    }

    makeId(length: number): string {
      let i;
      let randomNum;
      let flooredNum;
      this.result = '';
      for (i = 0; i < length; i += 1) {
        randomNum = Math.random();
        flooredNum = Math.floor(randomNum * 10);
        this.result += CHARACTERS.charAt(flooredNum);
      }
      return this.result;
    }

    render() {
      const { viewport, sideNavExpanded = false } = this.state;

      const constructedProps: {|...InjectedProps, ...RefProps, ...Config|} = {
        viewport,
        sideNavExpanded,
        sideNavAppHandler: this.sideNavAppHandler,
        ...this.props,
      };

      if (!shouldSetInnerRef && constructedProps.innerRef) {
        constructedProps.ref = constructedProps.innerRef;
        delete constructedProps.innerRef;
      }
      return <Component {...constructedProps} />;
    }
  }

  WithManager.displayName = `WithManager(${componentName})`;

  return WithManager;
}

export default (wrapWithManager: <T: React$Config<{}, {}>>
  (Component: React$ComponentType<T>)
  => React$ComponentType<$Diff<T, InjectedProps>>);
